import { useLayoutEffect, useRef } from 'react';
import type { Mesh, Texture } from 'three';
import type { DisplayPanelConfig, Vector3Tuple } from '../types';

export interface InteriorDisplayPanelProps {
  panel: DisplayPanelConfig;
  texture: Texture | null | undefined;
  /** 판이 바라볼 지점. 없으면 회전을 건드리지 않습니다. */
  faceTowards: Vector3Tuple | null | undefined;
}

/**
 * 에셋에 화면이 없는 차량에 얹는 디스플레이 판.
 *
 * 왜 필요한가:
 *   Taycan 에셋은 대시보드가 통짜 셸이고 화면이 별개 면으로 모델링돼 있지 않습니다.
 *   측정해 보면 운전석 앞은 법선이 위(+Y)를 향한 가로 선반이라, 텍스처를 입힐
 *   수직면 자체가 없습니다. 실차의 프리스탠딩 디스플레이처럼 판을 세워 얹습니다.
 *
 * 왜 좌표를 차량 하위가 아니라 월드에 두는가:
 *   차량은 rotationY와 scale이 걸린 채로 씬에 들어갑니다. 판을 차량 하위에 넣으면
 *   그 변환이 한 번 더 먹어서 위치가 어긋납니다. 매니페스트의 좌표는 이미 앱
 *   좌표계(회전·스케일 적용 후) 실측값이므로 차량과 형제로 둡니다.
 */
export function InteriorDisplayPanel({ panel, texture, faceTowards }: InteriorDisplayPanelProps) {
  const meshRef = useRef<Mesh | null>(null);

  /**
   * 오일러 각을 직접 계산하지 않고 lookAt으로 돌립니다.
   *
   * 판이 향해야 하는 방향은 "운전자 눈"이라는 한 점으로 정의됩니다. 이걸 오일러
   * 각으로 옮기면 회전 순서(three 기본 XYZ) 때문에 부호를 틀리기 쉽고, 눈 위치를
   * 모델마다 조금씩 조정할 때 각도를 매번 다시 계산해야 합니다.
   */
  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (mesh === null || faceTowards === undefined || faceTowards === null) {
      return;
    }
    mesh.lookAt(faceTowards[0], faceTowards[1], faceTowards[2]);
  }, [faceTowards, panel.position]);

  if (texture === null || texture === undefined) {
    return null;
  }

  return (
    <mesh ref={meshRef} position={panel.position}>
      <planeGeometry args={panel.size} />
      {/*
        화면은 조명을 받는 물체가 아니라 스스로 빛을 내는 물체입니다.
        toneMapped={false}로 톤 매핑을 건너뛰어 배경 밝기와 무관하게 켜져 보입니다.
      */}
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  );
}
